import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  forwardRef,
  useContext,
} from "react";
import { LinearProgress, useMediaQuery, useTheme } from "@mui/material";
import WebViewer from "@pdftron/pdfjs-express-viewer";
import { PreferencesContext } from "../PreferencesContext";
import { BASE_URL } from "../Services/common/const";

import WhatsAppIcon from "@mui/icons-material/WhatsApp";

export interface PdfViewerProps {
  fileUrl: string;
  openInvoice: boolean;
  invoiceHtmlTitle: string;
  handleCloseInvoice: () => void;
  invoiceTitle: string;
  invoiceHtml: string;
  handleClickOpenSendEmail: (open: boolean, itemData: any) => void;
  handleClickOpenSendWhatsapp: (open: boolean, itemData: any) => void;
  itemData: any;
  handleDownload: () => void;
  isforSmallDevice: boolean;
  isLoading: boolean;
  fileName?: string;
  fileToken?: string;
}

export const PdfViewer = React.memo(
  forwardRef((props: PdfViewerProps, ref) => {
    const {
      invoiceHtml,
      invoiceTitle,
      handleDownload,
      handleCloseInvoice,
      handleClickOpenSendEmail,
      handleClickOpenSendWhatsapp,
      itemData,
      isforSmallDevice,
      isLoading,
      fileName,
    } = props;

    const viewer = useRef<HTMLDivElement>(null);
    const initializedRef = useRef(false);
    const [loading, setLoading] = useState(true);

    const { userContext, updateUserContext } = useContext(PreferencesContext);

    const base64ToBlob = (base64String: string): Blob => {
      try {
        const base64 = base64String.includes(",")
          ? base64String.split(",")[1]
          : base64String;
        const binaryString = window.atob(base64);
        const len = binaryString.length;
        const bytes = new Uint8Array(len);
        for (let i = 0; i < len; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        return new Blob([bytes], { type: "application/pdf" });
      } catch (error) {
        console.error("Invalid base64 string", error);
        return new Blob();
      }
    };

    const initializeViewer = useCallback(
      (blobUrl: string) => {
        if (!viewer.current || initializedRef.current || !blobUrl) return;
        initializedRef.current = true;
        setLoading(true);

        WebViewer(
          {
            path: "/webviewer/lib",
            licenseKey: "SCgY84A2o1asDz8os2fO",
            fullAPI: false,
            disableRedaction: true,
            disableMeasurement: true,
            disabledElements: [
              "searchButton",
              "menuButton",
              "selectToolButton",
              "viewControlsButton",
              "textSelectButton",
              "applyWatermark",
              "moreButton",
              "panToolButton",
            ],
            css: "/pdfstyle.css",
          },
          viewer.current
        ).then((instance) => {
          const { UI } = instance;

          UI.setFitMode(UI.FitMode.FitWidth);

          UI.loadDocument(blobUrl, {
            filename: invoiceTitle,
          });

          instance.Core.documentViewer.addEventListener(
            "documentLoaded",
            () => {
              setLoading(false);
              // instance.setPrintQuality(10);
              // instance.set(10);
            }
          );

          UI.setHeaderItems((header) => {
            const defaultHeader = header.getHeader("default");

            defaultHeader.push({
              img: "icon-header-print-line",
              type: "actionButton",
              onClick: () => UI.print(),
            });

            defaultHeader.push({
              img: "icon-header-download",
              type: "actionButton",
              onClick: () => {
                if (isforSmallDevice) {
                  handleDownload();
                } else {
                  UI.downloadPdf();
                }
              },
            });

            if (
              !["EWAYBILLORIGINAL", "EWAYBILLDRIVERCOPY", "EINVOICE"].includes(
                fileName ?? ""
              )
            ) {
              defaultHeader.push({
                img: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" transform="rotate(690)"><path d="m0 0h24v24h-24z" fill="none"></path><path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"></path></svg>',
                index: -1,
                type: "actionButton",
                element: "defaultHeaderButton",
                onClick: () => handleClickOpenSendEmail(true, itemData),
                style: {
                  marginBottom: "5px",
                },
              });
            }
            // defaultHeader.push({
            //   img: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" transform="rotate(690)"><path d="m0 0h24v24h-24z" fill="none"></path><path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"></path></svg>',
            //   index: -1,
            //   type: "actionButton",
            //   element: "defaultHeaderButton",
            //   onClick: () => handleClickOpenSendWhatsapp(true, itemData),
            //   style: {
            //     marginBottom: "5px",
            //   },
            // });
            defaultHeader.push({
              img: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#25D366"><path d="M0 0h24v24H0z" fill="none"/><path d="M16.72 13.83c-.27-.14-1.62-.8-1.87-.89-.25-.09-.43-.14-.61.14s-.7.89-.86 1.08c-.16.18-.32.2-.6.07-.27-.14-1.13-.42-2.15-1.34-.79-.7-1.32-1.56-1.48-1.83-.15-.27-.02-.41.12-.55.12-.12.27-.32.41-.48.14-.16.18-.27.27-.45.09-.18.05-.34-.02-.48-.07-.14-.61-1.46-.83-2-.22-.53-.44-.45-.61-.46h-.52c-.18 0-.48.07-.73.34s-.96.94-.96 2.3.98 2.67 1.12 2.85c.14.18 1.92 2.93 4.65 4.11.65.28 1.15.44 1.54.56.65.21 1.24.18 1.7.11.52-.08 1.62-.66 1.85-1.3.23-.64.23-1.19.16-1.3-.06-.12-.25-.18-.52-.31zM12 2C6.48 2 2 6.07 2 11.1c0 1.74.51 3.37 1.39 4.77L2 22l6.36-1.66c1.35.68 2.87 1.05 4.51 1.05 5.52 0 10-4.07 10-9.1S17.52 2 12 2z"/></svg>',
              index: -1,
              type: "actionButton",
              element: "defaultHeaderButton",
              onClick: () => {
                if (handleClickOpenSendWhatsapp) {
                  handleClickOpenSendWhatsapp(true, itemData);
                }else{
                  handlePrivateSendWhatsapp();
                }
              },
              style: {
                marginBottom: "5px",
              },
            });

            defaultHeader.push({
              img: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M0 0h24v24H0z" fill="none"/><path d="M6.41 6L12 11.59 17.59 6 19 7.41l-6 6 6 6-1.41 1.41L12 14.41l-6 6L4.59 19l6-6-6-6z"/></svg>',
              type: "actionButton",
              onClick: handleCloseInvoice,
            });
          });
        });
      },
      [
        invoiceTitle,
        handleDownload,
        handleClickOpenSendEmail,
        handleClickOpenSendWhatsapp,
        isforSmallDevice,
        fileName,
        itemData,
        handleCloseInvoice,
      ]
    );

    // useEffect(() => {
    //   if (!invoiceHtml) return;
    //   const blob = base64ToBlob(invoiceHtml);
    //   const objectUrl = URL.createObjectURL(blob);
    //   if (props?.fileToken) {
    //     initializeViewer(
    //       `${BASE_URL}/File/GetFile?token=${props?.fileToken}&filename=invoice.pdf`
    //     );
    //   } else {
    //     initializeViewer(objectUrl);
    //   }
    // }, [invoiceHtml, initializeViewer]);

    const handlePrivateSendWhatsapp = () => {
      const whatsappUrl = `https://web.whatsapp.com/send`;
      const isMobileDevice = /iPhone|Android|webOS|BlackBerry|iPad|iPod/.test(
        navigator.userAgent
      );
      if (isMobileDevice) {
        window.location.href = `whatsapp://send`;
      } else {
        window.open(whatsappUrl, "whatsappWindow");
      }
    }

    const isHtmlInvoice =
      typeof invoiceHtml === "string" &&
      invoiceHtml.trim().toLowerCase().includes("<html");

    useEffect(() => {
      const loadDocument = async () => {
        if (props?.fileToken) {
          const token = encodeURIComponent(props?.fileToken);

          const fileUrl = `${BASE_URL}/File/GetFile?token=${token}&filename=invoice.pdf`;

          initializeViewer(fileUrl);
        } else if (invoiceHtml && !isHtmlInvoice) {
          // Existing PDF flow
          const blob = base64ToBlob(invoiceHtml);

          const objectUrl = URL.createObjectURL(blob);

          initializeViewer(objectUrl);
        }
      };

      loadDocument();
    }, [
      props?.fileToken,
      invoiceHtml,
      initializeViewer,
      isHtmlInvoice,
    ]);

    // Optional: expose internal viewer reference via ref
    useImperativeHandle(ref, () => ({
      getViewerElement: () => viewer.current,
    }));



    return (
      <>
        {(loading || isLoading) && !isHtmlInvoice && (
          <LinearProgress
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              zIndex: 9999,
            }}
          />
        )}

        {isHtmlInvoice ? (
          <iframe
            title={props.invoiceHtmlTitle}
            srcDoc={invoiceHtml}
            style={{
              width: "100%",
              height: "100%",
              border: "none",
              display: "block",
            }}
          />
        ) : (
          <div
            ref={viewer}
            className="webviewer"
            style={{
              height: "calc(100dvh - 0px)",
              position: "relative",
              overflow: "hidden",
              WebkitOverflowScrolling: "touch",
            }}
          />
        )}
      </>
    );
  })
);
